/**
 * commentActionExecutors — les TROIS verbes de commentaire du canvas (#996).
 *
 * > Commenter, c'est le geste de l'avocat qui RELIT : soulever un doute, réclamer
 * > une pièce, signaler un risque — **sans toucher au texte**. L'agent savait
 * > écrire et corriger ; il ne savait pas annoter. `add_comments`,
 * > `reply_to_comment`, `resolve_comments`.
 *
 * ## ⚠️ Un commentaire n'est PAS une révision, et tout en découle
 *
 * Mesuré le 2026-08-16 (`test-results/tranche3-996.json`, mesure 16) : un
 * commentaire posé sous fenêtre `suggesting` est **accepté** et produit **ZÉRO**
 * révision suivie. Trois conséquences, toutes portées ici :
 *
 * 1. `mouvementAttendu:'aucun'` — sans lui, `buildVerifiedReceipt` exigerait par
 *    défaut une identité de révision NEUVE et rendrait `failed` sur un
 *    commentaire pourtant posé et relu.
 * 2. Les effets se déclarent `'none'`, jamais `'tracked'` : le receipt ne doit
 *    jamais conduire l'agent à dire à l'avocat d'aller accepter quelque chose
 *    dans Word — il n'y verra rien.
 * 3. La fenêtre de mode est ouverte en `'direct'` : basculer le document en
 *    `suggesting` autour d'un appel qui ne produit structurellement aucune
 *    révision serait un geste sans effet, et le code mentirait sur son intention.
 *
 * ## ⚠️ Les formes d'appel sont MESURÉES, aucune n'était devinable
 *
 * - **Poser** : `comments.create({target, text})` où `target` est **le champ
 *   `target` que `query.match` rend LUI-MÊME** dans `items[0].target`. Son
 *   `handle.ref` est refusé, `{kind:'block',…}` est refusé, `{kind:'textSearch',
 *   pattern}` est refusé, `{kind:'text', ref}` est refusé — tous rendent
 *   « target must be a TextAddress, TextTarget, SelectionTarget,
 *   TrackedChangeCommentTarget, or TextSearchCommentTarget object. » **On rend au
 *   fournisseur son propre objet, on n'en reconstruit pas un.**
 * - **Répondre** : `comments.create({parentCommentId, text})` **SANS target** —
 *   « Cannot combine parentCommentId with target. Replies do not take a target. »
 *   C'est pourquoi le schéma partagé de `reply_to_comment` n'a pas de sélecteur.
 * - **Résoudre** : `comments.patch({commentId, status:'resolved'})`. ⚠️ `id` est
 *   REFUSÉ (c'est `commentId`), et le vocabulaire de statut est **asymétrique** :
 *   on LIT `'open'`, on ÉCRIT `'resolved'` ou `'active'` — réécrire `'open'` tel
 *   qu'on vient de le lire est refusé. La traduction vit ici, et le modèle
 *   n'apprend qu'un seul mot, celui qu'il lit.
 * - ⚠️ **Résoudre la racine résout TOUT le fil**, réponses comprises (mesuré).
 *
 * ## Le juge est la RELECTURE, jamais le receipt
 *
 * `comments.create` rend `{success:true, id, inserted:[…], txId}` — un succès de
 * transport. La preuve est `comments.list` relue après coup. Doctrine constante
 * depuis #980, et elle vaut ici autant qu'ailleurs.
 */

import type { CanvasActionInput } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { idsDeRevision, type CanvasSnapshot, type CanvasSnapshotBlock } from './canvasSnapshot';
import { detailDuRefus, estUnRefus, resoudreLaCible } from './formatActionExecutors';
import { muterSousFences, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import { snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/** Les surfaces de commentaire du Document API, toutes mesurées présentes en 2.6.0. */
interface SurfacesDeCommentaire {
  comments?: {
    create?: MethodeProvider;
    patch?: MethodeProvider;
    list?: MethodeProvider;
  };
}

type ContexteDeCommentaire = CanvasActionContext & { doc: CanvasActionContext['doc'] & SurfacesDeCommentaire };

export type AddCommentsInput = Extract<CanvasActionInput, { action: 'add_comments' }>;
export type ReplyToCommentInput = Extract<CanvasActionInput, { action: 'reply_to_comment' }>;
export type ResolveCommentsInput = Extract<CanvasActionInput, { action: 'resolve_comments' }>;

/**
 * Le statut d'ÉCRITURE qui ferme un fil.
 *
 * ⚠️ Ne jamais y écrire `'open'` : c'est le mot de LECTURE, et le fournisseur le
 * refuse (« status must be "resolved" or "active" »). Les deux vocabulaires ne
 * coïncident pas, et c'est mesuré.
 */
const STATUT_RESOLU = 'resolved';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * L'identifiant du commentaire que la création vient de poser.
 *
 * Forme mesurée : `{success:true, id:'1', inserted:[{kind:'entity',
 * entityType:'comment', entityId:'1'}], txId}`. Les deux chemins sont lus parce
 * qu'ils sont tous deux publiés — mais l'absence des DEUX n'est pas un échec :
 * la postcondition retombe alors sur le texte.
 */
function idDuCommentaireCree(resultat: unknown): string | undefined {
  if (!isRecord(resultat)) return undefined;
  if (typeof resultat.id === 'string' && resultat.id.length > 0) return resultat.id;
  const inserted = Array.isArray(resultat.inserted) ? resultat.inserted : [];
  for (const entree of inserted) {
    if (isRecord(entree) && entree.entityType === 'comment' && typeof entree.entityId === 'string') {
      return entree.entityId;
    }
  }
  return undefined;
}

/**
 * La cible de commentaire, obtenue de `query.match` et RENDUE TELLE QUELLE.
 *
 * ⚠️ **C'est le point de tout ce verbe.** Le fournisseur n'accepte que l'objet
 * `target` qu'il a lui-même produit ; toute cible reconstruite — y compris à
 * partir du `handle.ref` qu'`add_hyperlink` utilise, et qui vit dans le MÊME
 * objet — est refusée. On ne la lit donc pas pour la
 * recomposer : on la transporte.
 */
async function cibleDeCommentaire(
  ctx: ContexteDeCommentaire,
  bloc: CanvasSnapshotBlock,
): Promise<{ ok: true; target: unknown } | { ok: false; code: string; message: string }> {
  const resultat = await ctx.doc.query?.match?.({
    type: 'text',
    pattern: bloc.fullText,
    caseSensitive: true,
    require: 'unique',
    within: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId },
  });
  const entrees = isRecord(resultat) && Array.isArray(resultat.items) ? resultat.items : [];
  const premier = entrees[0];
  const apercu = bloc.fullText.length > 80 ? `${bloc.fullText.slice(0, 80)}…` : bloc.fullText;
  if (!isRecord(premier) || premier.target === undefined) {
    return {
      ok: false,
      code: 'SELECTOR_NOT_FOUND',
      message: `add_comments a été REFUSÉ avant toute écriture : le bloc résolu (nodeId=${bloc.nodeId}) ne rend `
        + `aucune ancre de texte pour « ${apercu} ». Rien n'a été modifié. Un commentaire s'ancre sur du TEXTE, `
        + 'jamais sur un bloc, et seule query.match sait produire l\'ancre que l\'éditeur accepte. '
        + 'Le texte du bloc a changé depuis sa lecture, ou une différence invisible (espace insécable, suppression '
        + 'suivie, retour à la ligne) empêche l\'appariement. Relance canvas_inspect pour lire le texte ACTUEL du '
        + 'bloc, puis vise-le à nouveau.',
    };
  }
  return { ok: true, target: premier.target };
}

/** Le refus de capacité, rédigé une fois pour les trois verbes. */
function refusDeCapacite(
  intent: AddCommentsInput['action'] | ReplyToCommentInput['action'] | ResolveCommentsInput['action'],
  resume: CanvasSnapshot,
  surface: string,
  contournement: string,
): CanvasActionReceipt {
  return abortedBeforeMutation({
    intent,
    snapshot: toCanvasSnapshotSummary(resume),
    code: 'CAPABILITY_UNAVAILABLE',
    message: `${intent} exige editor.doc.${surface}, que cet éditeur n'expose pas. Rien n'a été modifié et relancer `
      + `la même action échouera à l'identique. ${contournement}`,
  });
}

/**
 * Commenter un passage que l'avocat a écrit (#996).
 *
 * ⚠️ Un bloc VIDE n'a aucun texte à ancrer, donc aucune cible possible. Le refus
 * porte son propre code : le sélecteur a RÉSOLU (le bloc existe, il est
 * simplement vide), donc `SELECTOR_NOT_FOUND` enverrait chercher une erreur de
 * ciblage qui n'existe pas (#980).
 */
export async function executeAddComments(
  ctx: ContexteDeCommentaire,
  input: AddCommentsInput,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'add_comments');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const cible = resolue.target;
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.comments?.create !== 'function') {
    return refusDeCapacite(input.action, pre, 'comments.create',
      'Il n\'existe aucun contournement : écrire la remarque DANS le texte de l\'acte la ferait partir au tribunal. '
      + 'Dis la remarque à l\'avocat dans la conversation.');
  }
  if (typeof ctx.doc.query?.match !== 'function') {
    return refusDeCapacite(input.action, pre, 'query.match',
      'C\'est la seule surface qui produise l\'ancre de texte que comments.create accepte : sans elle, aucun '
      + 'commentaire ne peut être posé. Dis la remarque à l\'avocat dans la conversation.');
  }
  if (cible.fullText.trim().length === 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'BLOCK_EMPTY',
      message: `add_comments ne peut pas commenter un bloc VIDE (nodeId=${cible.nodeId}) : un commentaire s'ancre sur `
        + 'du TEXTE, et il n\'y en a aucun ici. Rien n\'a été modifié. Vise le bloc qui porte réellement le passage '
        + 'à commenter.',
      // `reinspect` serait faux : relire rendra le même bloc vide (#991).
      nextAction: 'Vise un bloc qui contient du texte.',
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. Le bloc est vide par nature ; ré-inspecter rendrait le même bloc.',
      },
    });
  }

  const ancre = await cibleDeCommentaire(ctx, cible);
  if (!ancre.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: ancre.code,
      message: ancre.message,
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'add_comments:comments.create',
    // ⚠️ `'direct'` : un commentaire ne produit AUCUNE révision, quel que soit le
    // mode du document (mesuré). Annoncer `tracked` ferait croire au lecteur que
    // ce verbe est concerné par le régime de révision.
    { changeMode: 'direct', expectedRevision: attendue },
    // ⚠️ Les options de `comments.*` sont un `RevisionGuardOptions` — pas de
    // `changeMode` : l'y mettre serait un champ inconnu.
    () => ctx.doc.comments?.create?.({ target: ancre.target, text: input.text }, { expectedRevision: attendue }),
  );

  const post = await snapshot(ctx);
  ctx.verifierEpoque('add_comments:receipt');
  const idNeuf = idDuCommentaireCree(resultat);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(cible, resolue.candidateCount)],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    // Un commentaire ne pose aucune révision — exiger une identité neuve ferait
    // rendre `failed` à un commentaire posé et relu.
    mouvementAttendu: 'aucun',
    verification: [controleDePose('add_comments', post, idNeuf, input.text, resultat)],
    effects: [{
      kind: 'add_comments',
      // JAMAIS `'tracked'` : il n'y a rien à accepter ni à refuser (mesuré).
      reviewability: 'none',
      description: `Commentaire posé sur le bloc « ${cible.text.slice(0, 60)} ».`,
      nodeIds: [cible.nodeId],
    }],
  });
}

/**
 * La postcondition commune à `add_comments` et `reply_to_comment` : le
 * commentaire neuf doit se RELIRE.
 *
 * L'identifiant rendu par la création est le juge quand il existe ; sinon le
 * texte, qui est la seule autre chose qu'on connaisse de ce commentaire.
 */
function controleDePose(
  verbe: string,
  post: CanvasSnapshot,
  idNeuf: string | undefined,
  texte: string,
  resultat: unknown,
): VerificationCheck {
  const parId = idNeuf !== undefined && post.comments.some((commentaire) => commentaire.commentId === idNeuf);
  const parTexte = post.comments.some((commentaire) => commentaire.text === texte);
  const trouve = parId || parTexte;
  return {
    name: 'comment_relu',
    passed: trouve,
    message: trouve
      ? `Relecture : le commentaire ${idNeuf === undefined ? 'posé' : `(commentId=${idNeuf})`} figure bien dans `
        + `comments.list (${post.comments.length} commentaire(s) relus).`
      : `${verbe} a été annoncé en succès par l'éditeur (${detailDuRefus(resultat)}), mais la relecture de `
        + `comments.list ne rend AUCUN commentaire ${idNeuf === undefined ? 'portant ce texte' : `d'identifiant "${idNeuf}"`} `
        + `sur les ${post.comments.length} relus. Le commentaire n'est donc pas prouvé posé. `
        + 'Relis le document par canvas_inspect en incluant le domaine "comments" avant toute autre action — ne '
        + 'rejoue pas ce verbe à l\'identique, il poserait un doublon si le premier avait bien été enregistré.',
  };
}

/**
 * Répondre dans un fil de commentaires (#996).
 *
 * ⚠️ **Aucune cible, jamais.** Le fournisseur refuse explicitement de combiner
 * `parentCommentId` et `target` : une réponse appartient à son fil, pas à un
 * passage. C'est pour cela que le schéma partagé n'offre aucun sélecteur — en
 * offrir un ferait construire au modèle un appel refusé.
 */
export async function executeReplyToComment(
  ctx: ContexteDeCommentaire,
  input: ReplyToCommentInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.comments?.create !== 'function') {
    return refusDeCapacite(input.action, pre, 'comments.create',
      'Dis la réponse à l\'avocat dans la conversation : il n\'existe aucun contournement dans le document.');
  }

  // ⚠️ Refus NOMMÉ : un identifiant de commentaire ne s'invente pas. Le message
  // dit combien de commentaires existent et par quel appel les obtenir — même
  // rédaction que pour les identifiants de révision (#994), et pour la même
  // raison : le modèle lit ce refus hors contexte.
  if (!pre.comments.some((commentaire) => commentaire.commentId === input.commentId)) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `reply_to_comment a été REFUSÉ avant toute écriture : aucun commentaire d'identifiant `
        + `"${input.commentId}" n'existe dans ce document. Il en compte ${pre.comments.length}. Rien n'a été modifié. `
        + 'Un commentId ne se devine ni ne se compose : lance canvas_inspect avec includeDomains contenant '
        + '"comments" et reprends la valeur "commentId" telle quelle.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'reply_to_comment:comments.create',
    { changeMode: 'direct', expectedRevision: attendue },
    // ⚠️ **Ni `target`, ni `selector`, ni rien qui désigne un passage.**
    () => ctx.doc.comments?.create?.({ parentCommentId: input.commentId, text: input.text }, { expectedRevision: attendue }),
  );

  const post = await snapshot(ctx);
  ctx.verifierEpoque('reply_to_comment:receipt');
  const idNeuf = idDuCommentaireCree(resultat);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    mouvementAttendu: 'aucun',
    verification: [controleDePose('reply_to_comment', post, idNeuf, input.text, resultat)],
    effects: [{
      kind: 'reply_to_comment',
      reviewability: 'none',
      description: `Réponse ajoutée au fil du commentaire ${input.commentId}.`,
    }],
  });
}

/**
 * Clore des fils de commentaires (#996).
 *
 * ⚠️ **Résoudre la racine d'un fil résout TOUT le fil**, réponses comprises
 * (mesuré : le commentaire `0` et sa réponse `3` passent ensemble à `resolved`).
 * Le verbe porte donc sur une conversation, pas sur un message.
 */
export async function executeResolveComments(
  ctx: ContexteDeCommentaire,
  input: ResolveCommentsInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.comments?.patch !== 'function') {
    return refusDeCapacite(input.action, pre, 'comments.patch',
      'Les commentaires restent ouverts ; dis à l\'avocat lesquels ont été traités plutôt que de les fermer '
      + 'par un autre moyen — il n\'y en a pas.');
  }

  const connus = new Set(pre.comments.map((commentaire) => commentaire.commentId));
  const inconnus = input.commentIds.filter((id) => !connus.has(id));
  if (inconnus.length > 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `resolve_comments a été REFUSÉ avant toute écriture : ${inconnus.length} des ${input.commentIds.length} `
        + `identifiants demandés n'existent pas dans ce document : ${inconnus.map((id) => `"${id}"`).join(', ')}. `
        + `Il compte ${pre.comments.length} commentaire(s). Rien n'a été modifié — aucun des autres identifiants n'a `
        + 'été résolu non plus, le lot est refusé en bloc. Lance canvas_inspect avec includeDomains contenant '
        + '"comments" et reprends les valeurs "commentId" telles quelles.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const resultats: unknown[] = [];
  const echecs: Array<{ commentId: string; detail: string }> = [];

  for (const commentId of input.commentIds) {
    // Même raison que partout ailleurs : chaque patch fait avancer la révision,
    // donc la valeur attendue se relit avant chacun (#939, garde (b)).
    const attendue: RevisionDeDocument = await revisionCourante(ctx, pre.revision);
    const resultat = await muterSousFences(
      ctx,
      `resolve_comments:${commentId}`,
      { changeMode: 'direct', expectedRevision: attendue },
      // ⚠️ `commentId`, pas `id` (refusé) — et `status:'resolved'`, jamais
      // `'open'` : on n'écrit pas le mot qu'on lit.
      () => ctx.doc.comments?.patch?.({ commentId, status: STATUT_RESOLU }, { expectedRevision: attendue }),
    );
    resultats.push(resultat);
    if (estUnRefus(resultat)) echecs.push({ commentId, detail: detailDuRefus(resultat) });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('resolve_comments:receipt');

  const statutParId = new Map(post.comments.map((commentaire) => [commentaire.commentId, commentaire.status]));
  // ⚠️ Un identifiant ABSENT de la relecture n'est pas une preuve d'échec : la
  // page de `comments.list` est bornée. Seul un statut explicitement autre que
  // `resolved` contredit. Doctrine « signal absent ≠ signal négatif ».
  const contredits = input.commentIds.filter((id) => {
    const statut = statutParId.get(id);
    return statut !== undefined && statut !== 'resolved';
  });
  const prouves = input.commentIds.filter((id) => statutParId.get(id) === 'resolved').length;

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: resultats,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    mouvementAttendu: 'aucun',
    verification: [
      {
        name: 'comments_resolus',
        passed: contredits.length === 0,
        message: contredits.length === 0
          ? `Relecture : ${prouves}/${input.commentIds.length} commentaire(s) visé(s) se relisent en "resolved", aucun `
            + 'ne se relit autrement. (Un commentaire hors de la page de relecture n\'est ni prouvé ni infirmé.)'
          : `resolve_comments a été annoncé en succès par l'éditeur, mais la relecture rend ${contredits.length} `
            + `commentaire(s) TOUJOURS ouverts : ${contredits.map((id) => `"${id}"`).join(', ')}. `
            + 'Relis le document par canvas_inspect en incluant le domaine "comments" et dis à l\'avocat lesquels '
            + 'restent ouverts, plutôt que d\'annoncer une revue close.',
      },
      {
        name: 'patch_provider_calls',
        passed: echecs.length === 0,
        message: echecs.length === 0
          ? 'Chaque appel comments.patch a répondu en succès.'
          : `comments.patch a échoué pour : ${echecs.map((echec) => `${echec.commentId} → ${echec.detail}`).join(' ; ')}. `
            + 'Les autres commentaires du lot ont bien été traités.',
      },
    ],
    effects: [{
      kind: 'resolve_comments',
      reviewability: 'none',
      description: `${input.commentIds.length} fil(s) de commentaires marqués résolus (une racine résolue ferme aussi ses réponses).`,
    }],
  });
}

export const CANVAS_COMMENT_ACTION_EXECUTORS = {
  add_comments: executeAddComments,
  reply_to_comment: executeReplyToComment,
  resolve_comments: executeResolveComments,
};
